import type { PatientSummary } from '@quromedical/models';
import { useCallback, useMemo } from 'react';
import { strings } from 'strings';
import { trpc } from 'trpc';

export interface usePatientProps {
  patient?: PatientSummary;
  isLoading: boolean;
  error: string | undefined;
  invalidate: () => Promise<void>;
  cacheKey: string;
}

export const usePatient = (patientId: string) => {
  const {
    data,
    error: trpcError,
    refetch,
    isFetching,
    isLoading,
    isInitialLoading,
    isRefetching,
  } = trpc.patient.getById.useQuery({
    id: patientId,
  });

  const onSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const isInLoadingState = useMemo(() => {
    return isLoading || isFetching || isInitialLoading || isRefetching;
  }, [isFetching, isLoading, isInitialLoading, isRefetching]);

  const error = trpcError ? strings.GenericErrorMessage : undefined;

  const cacheKey = useMemo(() => `patient:${patientId}`, [patientId]);
  const patient = useMemo(() => data as PatientSummary | undefined, [data]);
  const generalPractitioner = useMemo(
    () =>
      patient?.generalPractitioner || {
        organizations: [],
        practitioners: [],
        careTeams: [],
      },
    [patient],
  );
  const gender = patient?.general.gender;

  return {
    organizations: generalPractitioner.organizations,
    practitioners: generalPractitioner.practitioners,
    careTeams: generalPractitioner.careTeams,
    general: patient?.general,
    gender,
    isLoading: isInLoadingState,
    invalidate: onSuccess,
    cacheKey,
    error,
  };
};
